import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiResponse } from '../types';
import type { AxiosWrapper } from '../';
import type { ApiRequestOptions } from './ApiRequestOptions';

const normalizeFetchHeaders = (rawHeaders: AxiosRequestConfig['headers']): Record<string, string> => {
    const headers: Record<string, string> = {};
    if (!rawHeaders) {
        return headers;
    }

    Object.entries(rawHeaders as Record<string, unknown>).forEach(([key, val]) => {
        if (val == null) {
            return;
        }
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
            headers[key] = String(val);
        }
    });

    return headers;
};

/**
 * Axios-адаптер, заменяющий XHR на fetch с keepalive:true.
 * Используется для запросов из beforeunload, браузер гарантирует доставку
 * даже после закрытия/перезагрузки страницы, чего XHR не обеспечивает.
 * Interceptors (авторизация,трансформация) отрабатывают штатно до вызова адаптера.
 */
const keepaliveFetchAdapter = async (config: AxiosRequestConfig): Promise<AxiosResponse> => {
    const baseURL = (config.baseURL ?? '').replace(/\/$/, '');
    const url = baseURL + (config.url ?? '');

    if (!url) {
        return Promise.reject(new Error('keepaliveFetchAdapter: empty url'));
    }

    const response = await fetch(url, {
        method: (config.method ?? 'GET').toUpperCase(),
        headers: normalizeFetchHeaders(config.headers),
        body: config.data ?? undefined,
        keepalive: true,
    });

    let data: unknown = null;
    try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
    });

    return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        config,
        request: null,
    } as AxiosResponse;
};

export const axiosRequest = <T>(
    domainName: string,
    options: ApiRequestOptions,
    axiosWrapper: AxiosWrapper,
): Promise<ApiResponse<T>> => {
    try {
        options.headers &&
            Object.keys(options.headers).forEach((header: string) => {
                if (options.headers && options.headers[header] === undefined) {
                    console.log('Deleted header', header);
                    delete options.headers[header];
                }
            });
    } catch (e) {
        console.error(e);
    }

    return axiosWrapper.request<T>({
        data: options.body,
        ...options,
        ...(options.keepalive && {
            adapter: keepaliveFetchAdapter,
        }),
    });
};
